import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, ShieldCheck, ShieldAlert, ShieldX, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from "xlsx";

interface ParsedRecord {
  seller_id: string;
  county: string;
  origin_state: string;
  recorded_date: string | null;
  pid: string;
  first_name: string;
  last_name: string;
  suffix: string;
  business_name: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  consideration: number | null;
  document_type: string;
  property_address: string;
  response: string;
  phone_number: string;
  email_address: string;
  household_income: string;
  tm_notes: string;
  tm_dropdown: string;
  mail_id: string;
  mail_date: string | null;
  mail_id2: string;
  mail_date2: string | null;
}

const COLUMN_MAP: Record<string, string> = {
  sellerid: "seller_id",
  county: "county",
  originstate: "origin_state",
  recordeddate: "recorded_date",
  recordingdate: "recorded_date",
  pid: "pid",
  parcelid: "pid",
  firstname: "first_name",
  lastname: "last_name",
  sellername: "seller_name",
  suffix: "suffix",
  businessname: "business_name",
  streetaddress: "street_address",
  city: "city",
  state: "state",
  zipcode: "zip_code",
  zip: "zip_code",
  consideration: "consideration",
  amount: "consideration",
  documenttype: "document_type",
  propertyaddress: "property_address",
  response: "response",
  phonenumber: "phone_number",
  emailaddress: "email_address",
  householdincome: "household_income",
  "tm notes": "tm_notes",
  tmnotes: "tm_notes",
  "tm dropdown": "tm_dropdown",
  tmdropdown: "tm_dropdown",
  "mail id": "mail_id",
  mailid: "mail_id",
  "mail date": "mail_date",
  maildate: "mail_date",
  "mail id2": "mail_id2",
  mailid2: "mail_id2",
  "mail date2": "mail_date2",
  maildate2: "mail_date2",
};

function parseExcelDate(val: any): string | null {
  if (!val) return null;
  if (typeof val === "number") {
    // Excel serial date
    const d = XLSX.SSF.parse_date_code(val);
    if (d) return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
  }
  const s = String(val).trim();
  if (!s) return null;
  // Try "23-Jun-25" format
  const match = s.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
  if (match) {
    const months: Record<string, string> = { jan:"01",feb:"02",mar:"03",apr:"04",may:"05",jun:"06",jul:"07",aug:"08",sep:"09",oct:"10",nov:"11",dec:"12" };
    const m = months[match[2].toLowerCase()];
    let y = match[3].length === 2 ? (parseInt(match[3]) > 50 ? "19" + match[3] : "20" + match[3]) : match[3];
    return `${y}-${m}-${match[1].padStart(2, "0")}`;
  }
  // Try standard date parse
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
  return null;
}

function parseConsideration(val: any): number | null {
  if (val == null) return null;
  const s = String(val).replace(/[$,\s]/g, "");
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

function inferLetterType(campaign: string): string {
  const lower = (campaign || "").toLowerCase();
  if (lower.includes("pc") || lower.includes("postcard") || lower.includes("post card")) return "postcard";
  if (lower.includes("insert")) return "insert";
  if (lower.includes("env") || lower.includes("envelope")) return "envelope";
  if (lower.includes("letter")) return "letter";
  return "other";
}

export function ExcelUpload({ onImportComplete }: { onImportComplete?: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedRecord[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<{ total: number; inserted: number; skipped: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [melissaCleaning, setMelissaCleaning] = useState(false);
  const [melissaResults, setMelissaResults] = useState<any[] | null>(null);
  const [melissaSummary, setMelissaSummary] = useState<{ good: number; review: number; bad: number } | null>(null);

  const handleFile = useCallback((f: File) => {
    setFile(f);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target?.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });

        const records: ParsedRecord[] = raw.map((row) => {
          const mapped: any = {};
          for (const key of Object.keys(row)) {
            const normalized = key.toLowerCase().replace(/[^a-z0-9 ]/g, "").trim();
            const dbCol = COLUMN_MAP[normalized];
            if (dbCol) mapped[dbCol] = row[key];
          }
          // Handle seller_name → first_name/last_name split
          let firstName = String(mapped.first_name || "");
          let lastName = String(mapped.last_name || "");
          if (!firstName && !lastName && mapped.seller_name) {
            const fullName = String(mapped.seller_name).trim();
            const parts = fullName.split(/\s+/);
            if (parts.length >= 2) {
              lastName = parts[0]; // "SMITH JOHN" → last=SMITH
              firstName = parts.slice(1).join(" "); // first=JOHN
            } else {
              lastName = fullName;
            }
          }

          // Auto-generate seller_id if missing
          let sellerId = String(mapped.seller_id || "");
          if (!sellerId) {
            const nameKey = `${lastName}-${firstName}`.replace(/\s+/g, '').toUpperCase();
            const addrKey = String(mapped.street_address || "").replace(/\s+/g, '').substring(0, 20).toUpperCase();
            const county = String(mapped.county || "").toUpperCase();
            sellerId = `${county}-${nameKey}-${addrKey}`;
          }

          return {
            seller_id: sellerId,
            county: String(mapped.county || ""),
            origin_state: String(mapped.origin_state || mapped.state || ""),
            recorded_date: parseExcelDate(mapped.recorded_date),
            pid: String(mapped.pid || ""),
            first_name: firstName,
            last_name: lastName,
            suffix: String(mapped.suffix || ""),
            business_name: String(mapped.business_name || ""),
            street_address: String(mapped.street_address || ""),
            city: String(mapped.city || ""),
            state: String(mapped.state || ""),
            zip_code: String(mapped.zip_code || ""),
            consideration: parseConsideration(mapped.consideration),
            document_type: String(mapped.document_type || ""),
            property_address: String(mapped.property_address || ""),
            response: String(mapped.response || ""),
            phone_number: String(mapped.phone_number || ""),
            email_address: String(mapped.email_address || ""),
            household_income: String(mapped.household_income || ""),
            tm_notes: String(mapped.tm_notes || ""),
            tm_dropdown: String(mapped.tm_dropdown || ""),
            mail_id: String(mapped.mail_id || ""),
            mail_date: parseExcelDate(mapped.mail_date),
            mail_id2: String(mapped.mail_id2 || ""),
            mail_date2: parseExcelDate(mapped.mail_date2),
          };
        });
        setParsedData(records);
        toast.success(`Parsed ${records.length} records from file`);
      } catch (err) {
        toast.error("Failed to parse Excel file");
        console.error(err);
      }
    };
    reader.readAsArrayBuffer(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleImport = async () => {
    if (!parsedData.length) return;
    setImporting(true);
    setProgress(0);
    let inserted = 0;
    let skipped = 0;
    const chunkSize = 500;

    try {
      for (let i = 0; i < parsedData.length; i += chunkSize) {
        const chunk = parsedData.slice(i, i + chunkSize);

        const mailingRecords = chunk.map((r) => ({
          seller_id: r.seller_id,
          county: r.county || null,
          origin_state: r.origin_state || null,
          recorded_date: r.recorded_date,
          pid: r.pid || null,
          first_name: r.first_name || null,
          last_name: r.last_name || null,
          suffix: r.suffix || null,
          business_name: r.business_name || null,
          street_address: r.street_address || null,
          city: r.city || null,
          state: r.state || null,
          zip_code: r.zip_code || null,
          consideration: r.consideration,
          document_type: r.document_type || null,
          property_address: r.property_address || null,
          response: r.response || null,
          phone_number: r.phone_number || null,
          email_address: r.email_address || null,
          household_income: r.household_income || null,
          tm_notes: r.tm_notes || null,
          tm_dropdown: r.tm_dropdown || null,
          mailing_status: r.response ? "responded" : "active",
          times_mailed: (r.mail_id ? 1 : 0) + (r.mail_id2 ? 1 : 0),
          last_mailed_date: r.mail_date2 || r.mail_date || null,
        }));

        const { data: insertedRecords, error } = await supabase
          .from("mailing_records")
          .upsert(mailingRecords, { onConflict: "seller_id", ignoreDuplicates: false })
          .select("id, seller_id");

        if (error) {
          console.error("Insert error:", error);
          toast.error(`Error at row ${i}: ${error.message}`);
          break;
        }

        inserted += insertedRecords?.length || 0;

        // Now insert mailing history for this chunk
        if (insertedRecords?.length) {
          const sellerIdMap = new Map(insertedRecords.map((r: any) => [r.seller_id, r.id]));
          const historyRows: any[] = [];

          for (const r of chunk) {
            const recordId = sellerIdMap.get(r.seller_id);
            if (!recordId) continue;

            if (r.mail_id) {
              historyRows.push({
                mailing_record_id: recordId,
                campaign_name: r.mail_id,
                mailed_date: r.mail_date,
                mailing_round: 1,
                letter_type: inferLetterType(r.mail_id),
              });
            }
            if (r.mail_id2) {
              historyRows.push({
                mailing_record_id: recordId,
                campaign_name: r.mail_id2,
                mailed_date: r.mail_date2,
                mailing_round: 2,
                letter_type: inferLetterType(r.mail_id2),
              });
            }
          }

          if (historyRows.length > 0) {
            // Delete existing history for these records first (for re-imports)
            const recordIds = [...new Set(historyRows.map(h => h.mailing_record_id))];
            await supabase.from("mailing_history").delete().in("mailing_record_id", recordIds);
            
            const { error: histError } = await supabase.from("mailing_history").insert(historyRows);
            if (histError) console.error("History insert error:", histError);
          }
        }

        setProgress(Math.round(((i + chunkSize) / parsedData.length) * 100));
      }

      setImportResult({ total: parsedData.length, inserted, skipped: parsedData.length - inserted });
      toast.success(`Import complete: ${inserted} records imported`);
      onImportComplete?.();
    } catch (err) {
      console.error(err);
      toast.error("Import failed");
    } finally {
      setImporting(false);
      setProgress(100);
    }
  };

  const preview = parsedData.slice(0, 50);

  const handleMelissaClean = async () => {
    if (!parsedData.length) return;
    setMelissaCleaning(true);
    try {
      // Send records to Melissa edge function in chunks
      const chunkSize = 100;
      const allResults: any[] = [];

      for (let i = 0; i < parsedData.length; i += chunkSize) {
        const chunk = parsedData.slice(i, i + chunkSize);
        const records = chunk.map(r => ({
          seller_id: r.seller_id,
          first_name: r.first_name,
          last_name: r.last_name,
          business_name: r.business_name,
          street_address: r.street_address,
          city: r.city,
          state: r.state,
          zip_code: r.zip_code,
          county: r.county,
          origin_state: r.origin_state,
          recorded_date: r.recorded_date,
          pid: r.pid,
          suffix: r.suffix,
          consideration: r.consideration,
          document_type: r.document_type,
          property_address: r.property_address,
        }));

        const { data, error } = await supabase.functions.invoke('melissa-address-verify', {
          body: { records },
        });

        if (error) throw error;
        if (data?.records) allResults.push(...data.records);
        setProgress(Math.round(((i + chunkSize) / parsedData.length) * 100));
      }

      setMelissaResults(allResults);
      const good = allResults.filter(r => r.status === 'good').length;
      const review = allResults.filter(r => r.status === 'review').length;
      const bad = allResults.filter(r => r.status === 'bad').length;
      setMelissaSummary({ good, review, bad });
      toast.success(`Melissa verification complete: ${good} verified, ${review} review, ${bad} bad`);
    } catch (err) {
      console.error('Melissa error:', err);
      toast.error(err instanceof Error ? err.message : 'Melissa cleaning failed');
    } finally {
      setMelissaCleaning(false);
      setProgress(0);
    }
  };

  const handleMelissaImport = async () => {
    if (!melissaResults) return;
    const toImport = melissaResults.filter(r => r.status === 'good' || r.status === 'review');
    if (!toImport.length) return;

    setImporting(true);
    setProgress(0);
    let inserted = 0;
    const chunkSize = 500;

    try {
      for (let i = 0; i < toImport.length; i += chunkSize) {
        const chunk = toImport.slice(i, i + chunkSize);
        const mailingRecords = chunk.map(r => ({
          seller_id: r.seller_id,
          county: r.county || null,
          origin_state: r.origin_state || null,
          recorded_date: r.recorded_date,
          pid: r.pid || null,
          first_name: r.first_name || null,
          last_name: r.last_name || null,
          suffix: r.suffix || null,
          business_name: r.business_name || null,
          street_address: r.md_address || r.street_address || null,
          city: r.md_city || r.city || null,
          state: r.md_state || r.state || null,
          zip_code: r.md_zip || r.zip_code || null,
          consideration: r.consideration,
          document_type: r.document_type || null,
          property_address: r.property_address || null,
          mailing_status: "active",
          times_mailed: 0,
        }));

        const { data, error } = await supabase
          .from("mailing_records")
          .upsert(mailingRecords, { onConflict: "seller_id", ignoreDuplicates: false })
          .select("id");

        if (error) {
          console.error("Insert error:", error);
          toast.error(`Error at batch ${Math.floor(i / chunkSize) + 1}: ${error.message}`);
          break;
        }
        inserted += data?.length || 0;
        setProgress(Math.round(((i + chunkSize) / toImport.length) * 100));
      }

      const badCount = melissaResults.filter(r => r.status === 'bad').length;
      setImportResult({ total: melissaResults.length, inserted, skipped: badCount });
      toast.success(`Import complete: ${inserted} records imported with Melissa-verified addresses`);
      onImportComplete?.();
    } catch (err) {
      console.error(err);
      toast.error("Import failed");
    } finally {
      setImporting(false);
      setProgress(100);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5" />
          Import from Excel
        </CardTitle>
        <CardDescription>Upload your Sellerinfo.xlsx file to import mailing records</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!parsedData.length && (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => {
              const input = document.createElement("input");
              input.type = "file";
              input.accept = ".xlsx,.xls,.csv";
              input.onchange = (e: any) => { if (e.target.files[0]) handleFile(e.target.files[0]); };
              input.click();
            }}
          >
            <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm font-medium">Drop your Excel file here or click to browse</p>
            <p className="text-xs text-muted-foreground mt-1">Supports .xlsx, .xls, .csv</p>
          </div>
        )}

        {parsedData.length > 0 && !importResult && !melissaResults && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">
                {file?.name} — {parsedData.length.toLocaleString()} records found
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setParsedData([]); setFile(null); setMelissaResults(null); setMelissaSummary(null); }}>
                  Clear
                </Button>
                <Button size="sm" variant="outline" onClick={handleImport} disabled={importing || melissaCleaning}>
                  Import Raw (Skip Melissa)
                </Button>
                <Button size="sm" onClick={handleMelissaClean} disabled={importing || melissaCleaning}>
                  {melissaCleaning ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Cleaning with Melissa...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="mr-2 h-4 w-4" />
                      Clean with Melissa & Import
                    </>
                  )}
                </Button>
              </div>
            </div>

            {(importing || melissaCleaning) && <Progress value={progress} className="h-2" />}

            <div className="border rounded-md overflow-auto max-h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SellerID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>County</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Doc Type</TableHead>
                    <TableHead>Consideration</TableHead>
                    <TableHead>Mail 1</TableHead>
                    <TableHead>Mail 2</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{r.seller_id}</TableCell>
                      <TableCell>{r.first_name} {r.last_name}</TableCell>
                      <TableCell>{r.county}</TableCell>
                      <TableCell>{r.origin_state}</TableCell>
                      <TableCell>{r.document_type}</TableCell>
                      <TableCell>{r.consideration ? `$${r.consideration.toLocaleString()}` : ""}</TableCell>
                      <TableCell className="text-xs">{r.mail_id}{r.mail_date ? ` (${r.mail_date})` : ""}</TableCell>
                      <TableCell className="text-xs">{r.mail_id2}{r.mail_date2 ? ` (${r.mail_date2})` : ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {parsedData.length > 50 && (
              <p className="text-xs text-muted-foreground text-center">
                Showing first 50 of {parsedData.length.toLocaleString()} records
              </p>
            )}
          </>
        )}

        {melissaResults && !importResult && (
          <>
            {/* Melissa Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
                <ShieldCheck className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-lg font-bold">{melissaSummary?.good || 0}</p>
                  <p className="text-xs text-muted-foreground">Verified — will import</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
                <ShieldAlert className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="text-lg font-bold">{melissaSummary?.review || 0}</p>
                  <p className="text-xs text-muted-foreground">Review — will import</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50">
                <ShieldX className="h-5 w-5 text-destructive" />
                <div>
                  <p className="text-lg font-bold">{melissaSummary?.bad || 0}</p>
                  <p className="text-xs text-muted-foreground">Bad — skipping</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Melissa Results — {melissaResults.length} records</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setMelissaResults(null); setMelissaSummary(null); setParsedData([]); setFile(null); }}>
                  Clear
                </Button>
                <Button size="sm" onClick={handleMelissaImport} disabled={importing}>
                  {importing ? "Importing..." : `Import ${(melissaSummary?.good || 0) + (melissaSummary?.review || 0)} Records`}
                </Button>
              </div>
            </div>

            {importing && <Progress value={progress} className="h-2" />}

            <div className="border rounded-md overflow-auto max-h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow className="text-xs">
                    <TableHead className="w-8">✓</TableHead>
                    <TableHead>SellerID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Original Address</TableHead>
                    <TableHead className="font-bold">MD Address</TableHead>
                    <TableHead className="font-bold">MD City</TableHead>
                    <TableHead className="font-bold">MD St</TableHead>
                    <TableHead className="font-bold">MD Zip</TableHead>
                    <TableHead>Result Codes</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {melissaResults.slice(0, 100).map((r, i) => (
                    <TableRow key={i} className={`text-xs ${r.status === 'bad' ? 'opacity-40' : ''}`}>
                      <TableCell>
                        {r.status === 'good' ? <ShieldCheck className="h-3.5 w-3.5 text-green-600" /> :
                         r.status === 'review' ? <ShieldAlert className="h-3.5 w-3.5 text-yellow-600" /> :
                         <ShieldX className="h-3.5 w-3.5 text-destructive" />}
                      </TableCell>
                      <TableCell className="font-mono">{r.seller_id}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.first_name} {r.last_name}</TableCell>
                      <TableCell className="whitespace-nowrap max-w-[180px] truncate">{r.street_address}, {r.city} {r.state}</TableCell>
                      <TableCell className="whitespace-nowrap font-medium">{r.md_address}</TableCell>
                      <TableCell className="whitespace-nowrap font-medium">{r.md_city}</TableCell>
                      <TableCell className="font-medium">{r.md_state}</TableCell>
                      <TableCell className="font-medium">{r.md_zip}</TableCell>
                      <TableCell className="font-mono max-w-[150px] truncate">{r.md_results}</TableCell>
                      <TableCell>
                        <Badge variant={r.status === 'good' ? 'default' : r.status === 'review' ? 'secondary' : 'destructive'} className="text-[10px]">
                          {r.statusReason}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {melissaResults.length > 100 && (
              <p className="text-xs text-muted-foreground text-center">Showing first 100 of {melissaResults.length} records</p>
            )}
          </>
        )}

        {importResult && (
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg border">
            <CheckCircle className="h-5 w-5 text-primary" />
            <div>
              <p className="font-medium">Import Complete</p>
              <p className="text-sm text-muted-foreground">
                {importResult.inserted.toLocaleString()} records imported successfully
                {importResult.skipped > 0 && ` (${importResult.skipped} skipped/duplicates)`}
              </p>
            </div>
            <Button variant="outline" size="sm" className="ml-auto" onClick={() => { setParsedData([]); setFile(null); setImportResult(null); setMelissaResults(null); setMelissaSummary(null); }}>
              Import Another
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
